// Scoped persistence for the live invoice bridge; never interpret user language here.
const invoiceLiveActionsApi = (() => {
  const create = (deps: any) => {
    let issued: any = null, attemptedSend = false, sent: any = null, busy = false
    let reserved: any = null, reservedKey = '', issuedKey = ''
    const records = new Map<string, any>()
    const backend = () => deps.backend()
    const current = () => { if (!deps.isCurrent()) throw new Error('control.editorChanged') }
    const saveRecord = async (kind: string, input: any) => {
      current()
      const name = String(input?.name || '').trim()
      if (!name) throw new Error('control.incomplete')
      const existing = deps.records(kind).filter((r: any) =>
        (input.org && r.org === input.org) || String(r.name || '').trim().toLocaleLowerCase() === name.toLocaleLowerCase())
      if (existing.length > 1) throw new Error('live.ambiguousRecord')
      if (existing.length === 1) return existing[0]
      const key = JSON.stringify([kind, input])
      const draft = records.get(key) || { ...input, name, id: crypto.randomUUID() }
      records.set(key, draft)
      if (kind === 'product') {
        if (!['Vare', 'Tjeneste'].includes(input.type) || !Number.isFinite(input.price) || input.price < 0 ||
          !backend().getSalesVatOptions().some((v: any) => String(v.value) === input.vatSelection)) throw new Error('control.incomplete')
        const vat = backend().normalizeSalesVatOption(input.vatSelection)
        Object.assign(draft, { vat: vat.rate, vatTreatment: vat.treatment,
          salesAccountCode: deps.productAccount(input.vatSelection, input.type) })
      }
      const result = await (kind === 'customer' ? backend().saveCustomerRecord(draft) : backend().saveProductRecord(draft))
      if (!result?.id) throw new Error('control.failed')
      return result
    }
    const fulfill = async (step: any) => {
      current()
      if (busy) throw new Error('live.actionBusy')
      busy = true
      try {
        const b = backend(), s = deps.editor()
        if (issuedKey && b.editorDraftKey(s) !== issuedKey) throw new Error('control.editorChanged')
        if (sent) return sent
        const sending = step.action === 'send_invoice'
        if (!['faktura', 'tilbud', 'ordre'].includes(s.docType) || s.repeating || s.customer?.isList || (!issued && deps.blocked())) throw new Error('control.advancedInvoice')
        const stored = (b.appState?.invoices || []).find((i: any) => i.id === (issued?.id || s.sourceDraftId))
        if (stored?.sentAt) return { invoice: stored, emailSent: true, alreadySent: true }
        const email = String(step.recipientEmail || issued?.customer?.email || s.customer?.email || '').trim()
        if (sending && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !b.appState?.settings?.email)) throw new Error('live.emailRequired')
        // An uncertain delivery must be checked manually, never automatically retried.
        const deliveryKey = `plumme:invoice-live:delivery:${deps.businessId}:${issued?.id || s.sourceDraftId || reserved?.id || ''}`
        if (sending && (attemptedSend || deps.deliveryPending(deliveryKey))) throw new Error('live.deliveryUncertain')
        if (!issued) {
          if (stored && stored.status !== 'draft') issued = stored
          else {
            const key = b.editorDraftKey(s)
            if (reserved && key !== reservedKey) throw new Error('control.editorChanged')
            if (!reserved) {
              reserved = b.buildInvoiceFromEditor(s, 'completed')
              s.markDraftSaved(reserved.id, '')
              reservedKey = b.editorDraftKey(s)
            }
            const withAttachments = await b.uploadEditorAttachments(reserved, s.attachments, 'invoices')
            current()
            if (b.editorDraftKey(deps.editor()) !== reservedKey) throw new Error('control.editorChanged')
            issued = await b.upsertInvoice(withAttachments)
            if (!issued?.id || issued.status === 'draft') throw new Error('control.failed')
            s.clearWip()
          }
        }
        issuedKey = b.editorDraftKey(s)
        current()
        if (!sending) { deps.onIssued(issued); return { invoice: issued, emailSent: false } }
        attemptedSend = true
        const pendingKey = `plumme:invoice-live:delivery:${deps.businessId}:${issued.id}`
        deps.markDeliveryPending(pendingKey)
        const result = await b.sendInvoiceDocument({ ...issued, customer: { ...issued.customer, email } }, {
          deliveryMode: step.deliveryMode || 'attachment', liveDelivery: true,
        })
        if (!result?.emailSent) throw new Error('live.deliveryNotSent')
        sent = result
        deps.onSent?.(result.invoice || issued)
        // The assistant owns the receipt; the page only resets the sent editor.
        return result
      } finally { busy = false }
    }
    return { saveRecord, fulfill }
  }
  return { create }
})()
Object.assign(globalThis, { PlummeInvoiceLiveActions: invoiceLiveActionsApi })
